import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getHackIncident, getHackLifetime } from '../api';

// Hack case panel, shown inside Asset Flow Tracing when a tracked hack case
// is picked: summary, TrustFlow AI lifetime write-up, the case's pre-built
// traces (switch before/after), verified wallets with live balances
// (refreshed every 5 min) and the timeline. Incident data:
// backend/src/config/hackIncidents.js.
const isVi = (i18n) => (i18n.language || 'vi').startsWith('vi');
const loc = (o, i18n) => (o ? (isVi(i18n) ? o.vi || o.en : o.en || o.vi) : '');

const CHAIN_LABEL = { ethereum: 'Ethereum', bitcoin: 'Bitcoin', bnb: 'BNB Chain', solana: 'Solana', tron: 'Tron' };
const NATIVE = { ethereum: 'ETH', bitcoin: 'BTC', bnb: 'BNB', solana: 'SOL', tron: 'TRX' };
const EXPLORER = {
  ethereum: (a) => `https://etherscan.io/address/${a}`,
  bitcoin: (a) => `https://mempool.space/address/${a}`,
};
const ROLE_COLOR = {
  trigger: '#a855f7', exploiter: '#ef4444', holding: '#f97316', laundering: '#f59e0b', victim: '#64748b', defensive: '#10b981',
};
const REFRESH_MS = 5 * 60 * 1000;
const PHASES = [
  { key: 'preparation', icon: '🕵️' },
  { key: 'attack', icon: '💥' },
  { key: 'laundering', icon: '🌀' },
  { key: 'currentStatus', icon: '📍' },
  { key: 'outlook', icon: '🔭' },
];

// TrustFlow AI "lifetime" write-up (backend services/hackNarrative.js). The
// backend answers at once with the stored version and regenerates in the
// background when it's older than 6h — poll while `pending`.
function LifetimeSection({ incidentId }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true, timer = null;
    const poll = async () => {
      try {
        const d = await getHackLifetime(incidentId);
        if (!active) return;
        setData(d); setFailed(false);
        if (d.pending) timer = setTimeout(poll, 10000);
      } catch { if (active) setFailed(true); }
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [incidentId]);

  const phases = data?.phases;
  return (
    <section id="hack-lifetime" style={{ ...s.section, scrollMarginTop: 16 }}>
      <div style={s.aiHead}>
        <span style={s.sectionTitle}>🤖 {t('hackTracker.lifetimeTitle')}</span>
        {data?.generatedAt && <span style={s.aiMeta}>{t('hackTracker.lifetimeGenerated', { time: fmtTime(data.generatedAt, i18n) })}{data.pending ? ` · ${t('hackTracker.lifetimeRefreshing')}` : ''}</span>}
      </div>
      {!phases ? (
        <div style={s.hint}>
          {failed || data?.error ? t('hackTracker.lifetimeError') : t('hackTracker.lifetimePending')}
        </div>
      ) : (
        <div style={s.phaseList}>
          {PHASES.map(({ key, icon }) => phases[key] && (
            <div key={key} style={s.phase}>
              <div style={s.phaseTitle}>{icon} {t(`hackTracker.phase.${key}`)}</div>
              <div style={s.phaseText}>{loc(phases[key], i18n)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={s.hint}>{t('hackTracker.lifetimeDisclaimer')}</div>
    </section>
  );
}

function fmtNum(n, dp = 2) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: dp });
}
function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}
function fmtTime(iso, i18n) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(isVi(i18n) ? 'vi-VN' : 'en-US', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) + ' UTC';
}
const short = (a) => (a.length > 20 ? `${a.slice(0, 10)}…${a.slice(-6)}` : a);

// USD value of one wallet's live holdings (native coin + tracked stablecoins).
function walletUsd(chain, bal, prices) {
  if (!bal) return null;
  let usd = 0, any = false;
  if (bal.native != null && prices?.[NATIVE[chain]] != null) { usd += bal.native * prices[NATIVE[chain]]; any = true; }
  for (const [sym, v] of Object.entries(bal.tokens || {})) { usd += v * (prices?.[sym] ?? 0); any = true; }
  return any ? usd : null;
}

export default function HackCasePanel({ incidentId, activeTraceId, onPickTrace }) {
  const { t, i18n } = useTranslation();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try { setIncident(await getHackIncident(incidentId)); setError(false); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [incidentId]);
  useEffect(() => {
    setLoading(true); setIncident(null); load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (loading && !incident) return <div style={s.wrap}><div style={s.empty}>{t('hackTracker.loading')}</div></div>;
  if (error && !incident) return <div style={s.wrap}><div style={s.empty}>{t('hackTracker.error')}</div></div>;
  if (!incident) return null;

  const live = incident.live;
  const prices = live?.prices || {};
  const stolenUsd = incident.stolen.reduce((sum, x) => sum + x.amount * (prices[x.asset] ?? 0), 0);
  const attackerAddrs = incident.addresses.filter((a) => a.role !== 'victim' && a.role !== 'defensive');
  const stillHeldUsd = attackerAddrs.reduce((sum, a) => sum + (walletUsd(a.chain, live?.balances?.[a.address], prices) || 0), 0);
  const chains = [...new Set(incident.addresses.map((a) => a.chain))];
  const nowMs = Date.now();

  return (
    <div style={s.wrap}>

      <div style={s.hero}>
        <div style={s.heroTop}>
          <span style={s.heroName}>🚨 {loc(incident.title, i18n)}</span>
          <span style={incident.status === 'active' ? s.statusActive : s.statusClosed}>
            {incident.status === 'active' ? t('hackTracker.statusActive') : t('hackTracker.statusClosed')}
          </span>
          <span style={s.heroTime}>{fmtTime(incident.occurredAt, i18n)}</span>
        </div>
        <p style={s.heroSummary}>{loc(incident.summary, i18n)}</p>
      </div>

      <div style={s.cards}>
        <div style={s.card}>
          <span style={s.cardLabel}>{t('hackTracker.stolen')}</span>
          <span style={{ ...s.cardValue, color: '#ef4444' }}>{fmtUsd(stolenUsd)}</span>
          <span style={s.cardSub}>{incident.stolen.map((x) => `${fmtNum(x.amount, 0)} ${x.asset}`).join(' · ')}</span>
        </div>
        <div style={s.card}>
          <span style={s.cardLabel}>{t('hackTracker.frozen')}</span>
          <span style={{ ...s.cardValue, color: '#10b981' }}>{fmtUsd(incident.frozenUsd)}</span>
          <span style={s.cardSub}>{t('hackTracker.frozenSub')}</span>
        </div>
        <div style={s.card}>
          <span style={s.cardLabel}>{t('hackTracker.stillHeld')}</span>
          <span style={{ ...s.cardValue, color: '#f97316' }}>{live ? fmtUsd(stillHeldUsd) : '—'}</span>
          <span style={s.cardSub}>{t('hackTracker.stillHeldSub')}</span>
        </div>
        <div style={s.card}>
          <span style={s.cardLabel}>{t('hackTracker.chains')}</span>
          <span style={s.cardValue}>{chains.map((c) => CHAIN_LABEL[c] || c).join(', ')}</span>
          <span style={s.cardSub}>{live ? t('hackTracker.updated', { time: fmtTime(live.updatedAt, i18n) }) : t('hackTracker.liveUnavailable')}</span>
        </div>
      </div>

      <LifetimeSection incidentId={incident.id} />

      <section style={s.section}>
        <div style={s.sectionTitle}>{t('hackTracker.traceTitle')}</div>
        <div style={s.traceRow}>
          {incident.traces.map((tr) => (
            <button
              key={tr.id} type="button"
              style={tr.id === activeTraceId ? { ...s.traceBtn, ...s.traceBtnActive } : s.traceBtn}
              onClick={() => onPickTrace && onPickTrace(tr)}
            >
              🕸️ {loc(tr.title, i18n)}
              <span style={s.traceMeta}>{CHAIN_LABEL[tr.chain]} · {tr.from} → {tr.to} · {t('hackTracker.wallets', { count: tr.seeds.length })}</span>
            </button>
          ))}
        </div>
        <div style={s.hint}>{t('hackTracker.traceHint')}</div>
      </section>

      <section style={s.section}>
        <div style={s.sectionTitle}>{t('hackTracker.walletsTitle')}</div>
        {chains.map((chain) => (
          <div key={chain} style={{ marginBottom: 12 }}>
            <div style={s.chainTitle}>{CHAIN_LABEL[chain] || chain}</div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>{t('hackTracker.colRole')}</th>
                    <th style={s.th}>{t('hackTracker.colAddress')}</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>{t('hackTracker.colBalance')}</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>{t('hackTracker.colValue')}</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>{t('hackTracker.colLast')}</th>
                  </tr>
                </thead>
                <tbody>
                  {incident.addresses.filter((a) => a.chain === chain).map((a) => {
                    const bal = live?.balances?.[a.address];
                    const usd = walletUsd(chain, bal, prices);
                    const recent = bal?.lastActivity && nowMs - new Date(bal.lastActivity).getTime() < 60 * 60 * 1000;
                    return (
                      <tr key={a.address}>
                        <td style={s.td}>
                          <span style={{ ...s.role, color: ROLE_COLOR[a.role], borderColor: ROLE_COLOR[a.role] }}>{t(`hackTracker.role.${a.role}`, a.role)}</span>
                          <div style={s.addrLabel}>{loc(a.label, i18n)}</div>
                        </td>
                        <td style={s.td}>
                          {EXPLORER[chain]
                            ? <a href={EXPLORER[chain](a.address)} target="_blank" rel="noopener noreferrer" style={s.addr} title={a.address}>{short(a.address)} ↗</a>
                            : <span style={s.addr} title={a.address}>{short(a.address)}</span>}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right' }}>
                          <div>{bal?.native != null ? `${fmtNum(bal.native, 2)} ${NATIVE[chain]}` : '—'}</div>
                          {Object.entries(bal?.tokens || {}).map(([sym, v]) => <div key={sym} style={s.tokenLine}>{fmtNum(v, 0)} {sym}</div>)}
                        </td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{fmtUsd(usd)}</td>
                        <td style={{ ...s.td, textAlign: 'right' }}>
                          {bal?.pending
                            ? <span style={s.moving}>● {t('hackTracker.movingNow')}</span>
                            : <span style={recent ? s.moving : s.lastTime}>{fmtTime(bal?.lastActivity, i18n)}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        <div style={s.hint}>{t('hackTracker.frozenNote')}</div>
      </section>

      <section style={s.section}>
        <div style={s.sectionTitle}>{t('hackTracker.timelineTitle')}</div>
        <ol style={s.timeline}>
          {incident.timeline.map((ev) => (
            <li key={ev.at} style={s.tlItem}>
              <span style={s.tlTime}>{fmtTime(ev.at, i18n)}</span>
              <span style={s.tlText}>{isVi(i18n) ? ev.vi : ev.en}</span>
            </li>
          ))}
        </ol>
      </section>

      {incident.notes?.length > 0 && (
        <section style={s.section}>
          <div style={s.sectionTitle}>{t('hackTracker.notesTitle')}</div>
          {incident.notes.map((n, i) => <div key={i} style={s.note}>⚠ {loc(n, i18n)}</div>)}
        </section>
      )}

      <div style={s.sources}>
        {t('hackTracker.sources')}:{' '}
        {incident.sources.map((src, i) => (
          <React.Fragment key={src.url}>{i > 0 && ' · '}<a href={src.url} target="_blank" rel="noopener noreferrer" style={s.sourceLink}>{src.name}</a></React.Fragment>
        ))}
      </div>
    </div>
  );
}

const s = {
  wrap: { marginTop: 18 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  select: { fontSize: 12.5, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' },
  empty: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  hero: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 },
  heroTop: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 },
  heroName: { fontSize: 16, fontWeight: 800, color: 'var(--text)' },
  statusActive: { fontSize: 11, fontWeight: 800, color: '#fff', background: '#ef4444', borderRadius: 999, padding: '2px 9px' },
  statusClosed: { fontSize: 11, fontWeight: 800, color: '#fff', background: '#64748b', borderRadius: 999, padding: '2px 9px' },
  heroTime: { fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' },
  heroSummary: { margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text)' },
  cards: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  card: { flex: '1 1 210px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardLabel: { fontSize: 9.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  cardValue: { fontSize: 18, fontWeight: 800, color: 'var(--text)' },
  cardSub: { fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4 },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 },
  sectionTitle: { fontSize: 13.5, fontWeight: 800, color: 'var(--text)', marginBottom: 10 },
  traceRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  traceBtnActive: { background: 'var(--accent)', color: '#fff' },
  traceBtn: { cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2, textDecoration: 'none', fontSize: 13, fontWeight: 700, color: 'var(--text)', background: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 10, padding: '8px 12px' },
  traceMeta: { fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' },
  hint: { fontSize: 11, color: 'var(--text-dim)', marginTop: 8, lineHeight: 1.5 },
  chainTitle: { fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  th: { textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '8px', borderBottom: '1px solid var(--border)', color: 'var(--text)', verticalAlign: 'top' },
  role: { fontSize: 10.5, fontWeight: 800, border: '1px solid', borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' },
  addrLabel: { fontSize: 11, color: 'var(--text-muted)', marginTop: 4 },
  addr: { fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)', textDecoration: 'none', whiteSpace: 'nowrap' },
  tokenLine: { fontSize: 11, color: 'var(--text-muted)' },
  moving: { fontSize: 11.5, fontWeight: 800, color: '#ef4444', whiteSpace: 'nowrap' },
  lastTime: { fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' },
  timeline: { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 },
  tlItem: { fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' },
  tlTime: { fontWeight: 700, color: 'var(--text-muted)', marginRight: 8, whiteSpace: 'nowrap' },
  tlText: {},
  aiHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  aiMeta: { fontSize: 11, color: 'var(--text-dim)' },
  phaseList: { display: 'flex', flexDirection: 'column', gap: 10 },
  phase: { borderLeft: '3px solid var(--accent)', paddingLeft: 12 },
  phaseTitle: { fontSize: 12.5, fontWeight: 800, color: 'var(--text)', marginBottom: 3 },
  phaseText: { fontSize: 13, lineHeight: 1.65, color: 'var(--text)' },
  note: { fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.45)', borderRadius: 8, padding: '8px 12px', marginBottom: 6 },
  sources: { fontSize: 11, color: 'var(--text-dim)' },
  sourceLink: { color: 'var(--text-muted)' },
};
